import JsonLd from '@/components/JsonLd'
import { webPageSchema, breadcrumbSchema } from '@/lib/jsonLdSchemas'
import Navbar from '@/components/layout/Navbar'
import Footer from '@/components/layout/Footer'

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen flex flex-col bg-slate-50 font-sans text-primary selection:bg-accent selection:text-white">
      <JsonLd data={webPageSchema({ name: 'Privacy Policy', description: 'How Gigafish collects, uses, and protects your personal information.', path: '/privacy' })} />
      <JsonLd data={breadcrumbSchema([{ name: 'Privacy Policy', path: '/privacy' }])} />
      <Navbar />
      <main className="relative flex-1 pt-36 pb-28 md:pt-40 md:pb-32">
        <div className="absolute inset-0 bg-[url('/assets/bg-noise.png')] opacity-[0.035] mix-blend-multiply pointer-events-none" />
        <div className="container relative z-10 max-w-2xl mx-auto px-8">
          <h1 className="text-4xl md:text-5xl font-bold font-heading text-primary uppercase tracking-tight mb-12">
            Privacy Policy
          </h1>

          <div className="space-y-8 text-slate-600 leading-relaxed">
            <section>
              <h2 className="text-xl font-bold text-primary mb-4">1. Introduction</h2>
              <p>
                Gigafish (&quot;we&quot;, &quot;our&quot;, or &quot;us&quot;) is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website gigafish.co or use our services.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-primary mb-4">2. Information We Collect</h2>
              <p>We may collect the following types of information:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li><strong className="text-primary">Personal information:</strong> Name, email address, phone number, and business details when you contact us or submit a project enquiry</li>
                <li><strong className="text-primary">Usage data:</strong> IP address, browser type, pages visited, and time spent on our website</li>
                <li><strong className="text-primary">Newsletter signups:</strong> Email address when you subscribe to our mailing list</li>
                <li><strong className="text-primary">Account data:</strong> Login credentials and profile information when you use our client portal</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-primary mb-4">3. How We Use Your Information</h2>
              <p>We use your information to:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Respond to enquiries and provide our services</li>
                <li>Send newsletters and marketing communications (with your consent)</li>
                <li>Improve our website and user experience</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-primary mb-4">4. Cookies and Tracking</h2>
              <p>
                We use cookies and similar technologies to analyse site traffic and improve functionality. You can control cookie preferences through your browser settings.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-primary mb-4">5. Third-Party Sharing</h2>
              <p>
                We may share your information with service providers who assist our operations (e.g. hosting, analytics, email delivery). We do not sell your personal information to third parties.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-primary mb-4">6. Data Security</h2>
              <p>
                We implement appropriate technical and organisational measures to protect your personal information against unauthorised access, alteration, or destruction.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-primary mb-4">7. Your Rights</h2>
              <p>
                You have the right to access, correct, or delete your personal information. You may also opt out of marketing communications at any time. Contact us at hello@gigafish.co to exercise these rights.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-primary mb-4">8. Contact</h2>
              <p>
                For questions about this Privacy Policy, contact us at{' '}
                <a href="mailto:hello@gigafish.co" className="text-accent underline hover:no-underline">hello@gigafish.co</a>.
              </p>
            </section>
          </div>

          <hr className="border-slate-200 mt-12 mb-6" />
          <p className="text-slate-500 text-xs">Last updated: {new Date().toLocaleDateString('en-AU')}</p>
        </div>
      </main>
      <Footer />
    </div>
  )
}
